// components/StudentCommandBar/index.tsx
/**
 * StudentCommandBar — quick command input for students (UI Tier 3, C+D blend).
 *
 * Opens with "/" (outside text fields), runs plan commands through the same
 * student-scoped chat command pipeline (`useCommandExecution` →
 * commandParser/commandExecutor). Deliberately separate from the admin-only
 * ⌘K CommandPalette: administrators have different privileges than users, so
 * this component never grows admin verbs — it is the student vocabulary only.
 *
 * Interaction: Enter runs the command; success closes the bar (result arrives
 * as a toast from useCommandExecution), failure keeps it open with the error
 * inline so the command can be corrected. Escape closes.
 */

import { useEffect, useRef, useState } from 'react';
import * as RadixDialog from '@radix-ui/react-dialog';
import { Loader2, Terminal } from 'lucide-react';
import { useInstitutionTheme } from '../../contexts/InstitutionConfigContext';
import type { CommandExecutionResult } from '../../hooks/useCommandExecution';

export interface StudentCommandBarProps {
  isOpen: boolean;
  onClose: () => void;
  onExecute: (commandString: string) => Promise<CommandExecutionResult>;
}

const StudentCommandBar = ({ isOpen, onClose, onExecute }: StudentCommandBarProps) => {
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const exampleCode = useInstitutionTheme().exampleCourseCodes?.[0] ?? 'a course';

  // Fresh slate per open
  useEffect(() => {
    if (isOpen) {
      setValue('');
      setError(null);
      setRunning(false);
    }
  }, [isOpen]);

  const run = async (): Promise<void> => {
    const command = value.trim();
    if (!command || running) return;
    setRunning(true);
    setError(null);
    try {
      const result = await onExecute(command);
      if (result.success) {
        onClose();
      } else {
        setError(result.message);
      }
    } finally {
      setRunning(false);
    }
  };

  const HINTS = [
    `add ${exampleCode} to fall 2027`,
    `remove ${exampleCode}`,
    `move ${exampleCode} to spring 2028`,
    `bookmark ${exampleCode}`,
    'help',
  ];

  return (
    <RadixDialog.Root open={isOpen} onOpenChange={(next) => !next && onClose()}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-50 bg-black/30 animate-fade-in" />
        {/* -x-centered keyframes: plain `animate-scale-in` would drop the
            -translate-x-1/2 for the animation and flash the bar right of center. */}
        <RadixDialog.Content
          aria-label="Plan command"
          className="fixed left-1/2 top-[18vh] z-50 w-[calc(100vw-2rem)] max-w-xl -translate-x-1/2 animate-scale-in-centered-x focus:outline-none"
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            inputRef.current?.focus();
          }}
        >
          <RadixDialog.Description className="hidden" />
          <div className="bg-surface-card rounded-surface shadow-lg border border-line-soft overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-3">
              <Terminal className="w-4 h-4 text-ink-3 flex-shrink-0" aria-hidden="true" />
              <input
                ref={inputRef}
                type="text"
                value={value}
                onChange={(e) => {
                  setValue(e.target.value);
                  if (error) setError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void run();
                  }
                }}
                placeholder={`Type a plan command — try “add ${exampleCode} to fall”`}
                aria-label="Plan command input"
                className="flex-1 bg-transparent text-sm text-ink placeholder-ink-3 focus:outline-none"
                disabled={running}
              />
              {running && (
                <Loader2 className="w-4 h-4 text-ink-3 animate-spin flex-shrink-0" aria-hidden="true" />
              )}
            </div>

            {error ? (
              <div
                role="alert"
                className="px-4 py-2 border-t border-line-soft bg-status-critical/8 text-status-critical-fg text-xs"
              >
                {error}
              </div>
            ) : (
              <div className="px-4 py-2 border-t border-line-soft bg-surface-sunken/60 flex flex-wrap items-center gap-1.5">
                {HINTS.map((hint) => (
                  <button
                    key={hint}
                    type="button"
                    onClick={() => {
                      setValue(hint);
                      inputRef.current?.focus();
                    }}
                    className="text-[11px] px-2 py-0.5 rounded-full bg-surface-card border border-line-soft text-ink-2 hover:text-ink hover:border-ink-3/40 transition-colors"
                  >
                    {hint}
                  </button>
                ))}
                <span className="ml-auto text-[11px] text-ink-3">Enter to run · Esc to close</span>
              </div>
            )}
          </div>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
};

export default StudentCommandBar;
